import React, { useState, useMemo, useEffect } from "react";
import {
  Grid,
  Image,
  List,
  ListContent,
  ListHeader,
  ListItem,
  Modal,
  ModalContent,
  ModalHeader,
  Button,
  Checkbox,
  ModalActions,
  ListDescription,
  ListList,
} from "semantic-ui-react";
import { IConveyorBoxItemUI } from "../../../lib/interfaces/ConveyorBox/IConveyorBox";
import { getStructuredBoxItems } from "../../../utils/helpers/conveyor-box";

import styles from "./SkuPutawayModal.module.css";
import classNames from "classnames";

type Props = {
  items: IConveyorBoxItemUI[];
  finishSkuPutaway: (items: Record<number, { quantity: number }> | null) => void;
  isSelectionMode?: boolean;
  isForcedOpen: boolean;
  onClose: () => void;
};

const SkuPutawayModal = ({
  items,
  finishSkuPutaway,
  isSelectionMode = false,
  isForcedOpen = false,
  onClose,
}: Props) => {
  const filteredItems = items.filter(
    (item1) =>
      (item1.attributes.includes("RX") &&
        items.some(
          (item2) =>
            item2.sku.id === item1.orderSkuID && item2.deliveredQuantity < item2.confirmedQuantity
        )) ||
      (item1.attributes.includes("RXBAG") && item1.deliveredQuantity >= item1.confirmedQuantity) ||
      item1.attributes.length < 1
  );

  const [selectedItems, setSelectedItems] = useState<Record<number, { quantity: number }>>({});

  const onIncreaseQuantity = (id: number, quantity: number) => {
    setSelectedItems((prev) => {
      const next = { ...prev };
      const item = filteredItems.find((i) => i.id === id);
      if (!item) return next;
      const prevQty = next[id]?.quantity || 0;
      if (prevQty + quantity > item.confirmedQuantity) return next;
      next[id] = { quantity: prevQty + quantity };
      return next;
    });
  };

  const onDecreaseQuantity = (id: number, quantity: number) => {
    setSelectedItems((prev) => {
      const next = { ...prev };
      const current = next[id];
      const prevQty = current?.quantity || 0;
      if (current && prevQty - quantity < 1) {
        delete next[id];
      } else if (prevQty - quantity >= 1) {
        next[id] = { quantity: prevQty - quantity };
      }
      return next;
    });
  };

  const toggleBagSelection = (id: number) => {
    setSelectedItems((prev) => {
      const next = { ...prev };
      if (next[id]) {
        delete next[id];
      } else {
        next[id] = { quantity: 1 };
      }
      return next;
    });
  };

  const modalTitle = useMemo(
    () =>
      isSelectionMode
        ? "Välj artiklar som ska skickas i en annan order"
        : "Hantering av avbrutna artiklar",
    [isSelectionMode]
  );

  const onCloseHandler = () => {
    setSelectedItems({});
    onClose();
  };

  const { otc, bundles } = getStructuredBoxItems(items);

  return (
    <Modal
      dimmer="blurring"
      open={(!!filteredItems.length && !isSelectionMode) || isForcedOpen}
      size="small"
      onClose={onCloseHandler}
    >
      <ModalHeader>{modalTitle}</ModalHeader>
      <ModalContent>
        <Grid columns={isSelectionMode ? 1 : 2} divided>
          <Grid.Row>
            <Grid.Column>
              <List
                divided
                relaxed
                size="large"
                className={styles["rx-list"]}
                style={{ maxHeight: "400px", overflow: "auto" }}
              >
                {bundles.some((bundle) => bundle.isFinished) && (
                  <ListItem>
                    <ListContent>
                      <ListHeader>Förslutna RX-påsar</ListHeader>
                      <ListDescription>
                        Receptpåsar som har förslutits och är klara för utlämning eller vidare
                        hantering.
                      </ListDescription>
                      <ListList>
                        {bundles
                          .filter((b) => b.isFinished)
                          .map((bundle) => (
                            <ListItem
                              key={bundle.id}
                              className={classNames(styles["rx-item"], styles["rx-item--finished"])}
                            >
                              <Image avatar src={bundle.article.imageUrl} />
                              <ListContent>
                                <ListHeader>{bundle.article.name}</ListHeader>
                                <ListDescription>
                                  {bundle.sku.id} <b>&#x2713;</b>
                                </ListDescription>
                              </ListContent>
                            </ListItem>
                          ))}
                      </ListList>
                    </ListContent>
                  </ListItem>
                )}
                {bundles.some((bundle) => !bundle.isFinished) && (
                  <ListItem>
                    <ListContent>
                      <ListHeader>Pågående RX-påsar</ListHeader>
                      <ListDescription>
                        Receptpåsar som har öppnats i LRX och ännu inte har förslutits.
                      </ListDescription>
                      <ListList>
                        {bundles
                          .filter((b) => !b.isFinished)
                          .map((bundle) => (
                            <ListItem key={bundle.id} className={styles["rx-item"]}>
                              <Checkbox
                                className="rx-check"
                                checked={!!selectedItems[bundle.id]}
                                onChange={() => toggleBagSelection(bundle.id)}
                              />
                              <Image avatar src={bundle.article.imageUrl} />
                              <ListContent>
                                <ListHeader>{bundle.article.name}</ListHeader>
                                {bundle.sku.id}
                                <ListList>
                                  {bundle.items.map((item) => (
                                    <ListItem key={item.id} className={styles["rx-subitem"]}>
                                      <Image avatar src={item.article.imageUrl} />
                                      <ListContent>
                                        <ListHeader>{item.article.name}</ListHeader>
                                        {item.sku.id}
                                      </ListContent>
                                    </ListItem>
                                  ))}
                                </ListList>
                              </ListContent>
                            </ListItem>
                          ))}
                      </ListList>
                    </ListContent>
                  </ListItem>
                )}
                {otc.length > 0 && (
                  <ListItem>
                    <ListContent>
                      <ListHeader>ORC / Receptfritt</ListHeader>
                      <ListDescription>
                        Produkter som inte är kopplade till något recept och hanteras separat från
                        RX-påsar.
                      </ListDescription>
                      <ListList>
                        {otc.map((item) => (
                          <ListItem key={item.id} className={styles["rx-item"]}>
                            <div className={styles["quantity-control"]}>
                              <Button size="mini" onClick={() => onDecreaseQuantity(item.id, 1)}>
                                -
                              </Button>
                              <div className={styles["qty"]}>
                                {selectedItems[item.id]?.quantity ?? 0}
                              </div>{" "}
                              <Button size="mini" onClick={() => onIncreaseQuantity(item.id, 1)}>
                                +
                              </Button>
                            </div>

                            <Image avatar src={item.article.imageUrl} />
                            <ListContent>
                              <ListHeader>{item.article.name}</ListHeader>
                              <ListDescription>
                                <div className={styles["item-info"]}>
                                  <span className={styles["sku-id"]}>{item.sku.id}</span>
                                  <strong className={styles["item-stats"]}>
                                    Beställt: {item.confirmedQuantity} | i lådan: {item.adjustedPickedQuantity}
                                  </strong>
                                </div>
                              </ListDescription>
                            </ListContent>
                          </ListItem>
                        ))}
                      </ListList>
                    </ListContent>
                  </ListItem>
                )}
              </List>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </ModalContent>
      {isSelectionMode && (
        <ModalActions>
          <Button
            primary
            disabled={Object.keys(selectedItems).length === 0}
            onClick={() => {
              finishSkuPutaway(selectedItems);
              setSelectedItems({});
            }}
          >
            Fortsätt
          </Button>
          <Button negative onClick={onClose}>
            Avbryt
          </Button>
        </ModalActions>
      )}
    </Modal>
  );
};

export default SkuPutawayModal;
